<template>
  <table class="uk-table uk-table-divider" :class="{ 'row-enhance': rowEnhance }">
    <thead>
      <tr>
        <th
          v-for="item in header"
          :key="item.key"
          @click="sortBy(item.key, item.type)"
          :class="[`uk-table-${item.width}`, { 'uk-text-nowrap': item.width === 'shrink' }]"
          :style="{
            width: `${Number.isInteger(item.width) ? item.width + 'px' : false}`
          }"
        >
          <template v-if="item.type === 'selection'">
            <input type="checkbox" v-model="item.checked" @change="onSelectionAll(item.key, $event)" />
          </template>
          <template v-else-if="item.type !== 'button'">
            {{ item.title }}
          </template>
          <span
            v-if="item.type === 'text' && item.key === lastSortedKey"
            :uk-icon="sortOrders[item.key] > 0 ? 'arrow-up' : 'arrow-down'"
          />
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(row, index) in filteredDisplayDatas" :key="index">
        <td
          v-for="col in header"
          :key="col.key"
          class="col"
          @click.stop="col.type === 'button' ? 'javascript:void(0)' : onColumnClick(row.orgIndex, col.key, $event)"
        >
          <template v-if="col.type === 'button'">
            <template v-if="!col.render || col.render(row)">
              <TableActionButton
                :title="typeof col.title === 'function' ? col.title(row) : col.title"
                :color="col.color || 'primary'"
                :disabled="col.disabled && col.disabled(row)"
                @click.stop="onColumnClick(row.orgIndex, col.key, $event)"
                :width="Number.isInteger(col.width) ? col.width : 0"
              />
            </template>
          </template>
          <template v-else-if="col.type === 'renderer'">
            <template v-html="col.renderer(row[col.key], row, col, index)" />
          </template>
          <template v-else-if="col.type === 'avatar'">
            <Avatar :clickable="false" :image="row[col.key]" />
          </template>
          <template v-else-if="col.type === 'selection'">
            <input
              type="checkbox"
              :disabled="col.disabled && col.disabled(row)"
              :value="true"
              :checked="row[col.key]"
              @change="onSelectionChange($event, index)"
            />
          </template>
          <template v-else>
            <div v-for="str in (row[col.key] || '').split('\n')" :key="str">
              {{ str }}
            </div>
          </template>
        </td>
      </tr>
    </tbody>
  </table>
  <VueEternalLoading :load="load" v-model:is-initial="isInitial" margin="300px">
    <template #loading="{ isFirstLoad }">
      <template v-if="isFirstLoad">
        データ取得中です
      </template>
      <template v-else>
        読み込み中...
      </template>
    </template>
    <template #no-more>データをすべて表示しました</template>
    <template #no-results>データはありません</template>
  </VueEternalLoading>
</template>
<style lang="scss" scoped>
.col {
  vertical-align: middle !important;
}
table.row-enhance tbody tr:hover {
  background-color: #e1eefc;
}
.uk-icon[uk-icon="arrow-up"] {
  color: #cf51b4;
}
.uk-icon[uk-icon="arrow-down"] {
  fill: #57ac6f;
}
</style>
<script lang="ts">
import { Vue, Options } from "vue-class-component";
import { Prop, Emit, Watch } from "vue-property-decorator";
import TableActionButton from "../atoms/TableActionButton.vue";
import Avatar from "../atoms/Avatar.vue";
import { VueEternalLoading, LoadAction } from "@ts-pro/vue-eternal-loading";

@Options({
  components: { TableActionButton, Avatar, VueEternalLoading }
})
export default class Table extends Vue {
  @Prop({ type: Array, required: true }) header?: {
    type: "button" | "text" | "selection";
    sortable?: boolean;
    title?: string;
    key: string;
    width?: ("shrink" | "expand") | number;
    color?: string;
    disabled: Function;
  }[];
  @Prop({ type: Array }) tableData?: object[];
  displayData: { [key: string]: any }[] = [];
  @Prop({ type: Boolean }) rowEnhance = false;
  pageSize = 100;
  isInitial = false;
  @Prop({ type: Function }) callback?: Function;
  sortKey: string = "";
  sortOrders: { [key: string]: number } = {};
  lastSortedKey: string = "";
  @Prop({ type: Function }) allSelectionCallback?: Function;

  mounted() {
    if (this.header) {
      this.header.forEach(head => {
        if (head.key) this.sortOrders[head.key] = 1;
        if (!head.type) head.type = "text";
      });
    }
  }

  get filteredDisplayDatas() {
    let sortKey = this.sortKey;
    let order = this.sortOrders[sortKey] || 1;
    let displayData = this.displayData;
    if (sortKey && displayData) {
      displayData = displayData.slice().sort((a, b) => {
        a = a[sortKey];
        b = b[sortKey];
        return (a === b ? 0 : a > b ? 1 : -1) * order;
      });
    }
    return displayData;
  }

  @Emit("column-click")
  onColumnClick(index: number, key: string, event: Event) {
    const rowData = this.displayData[index];
    return { row: index, col: key, el: event && event.target, rowData };
  }
  @Emit("selection-change")
  onSelectionChange(event: Event, index: number) {
    const target = event.target as HTMLInputElement;
    return { index, value: target.checked };
  }
  @Watch("tableData")
  reset() {
    this.displayData = [];
    this.isInitial = true;
  }
  sortBy(key: string, type: string) {
    if (type !== "text") return;
    this.sortKey = key;
    this.sortOrders[key] = this.sortOrders[key] * -1;
    this.lastSortedKey = key;
  }
  /**
   * テーブル更新  updatedAtの降順でソートを行う
   * @param {LoadAction} action VueEternalLoadingのトリガーアクション
   * @return void
   */
  async load(action: LoadAction): Promise<any> {
    if (this.callback) {
      await this.callback()
        .then((loadedData: any) => {
          if (loadedData.length) {
            if (loadedData[0].updatedAt) {
              loadedData.sort(function(a: any, b: any) {
                if (a.updatedAt !== b.updatedAt) {
                  return a.updatedAt < b.updatedAt ? 1 : -1;
                }
                return 0;
              });
              loadedData = loadedData.map((d: any, index: number) => ({
                orgIndex: this.displayData.length ? index + this.displayData.length : index,
                ...d
              }));
            }
            this.displayData.push(...loadedData);
            action.loaded(loadedData.length);
          } else {
            action.noMore();
          }
        })
        .catch(() => {
          action.noMore();
        });
    } else {
      if (!this.tableData) return;
      let loadedData = this.tableData.slice(this.displayData.length, this.displayData.length + this.pageSize);
      if (loadedData.length) {
        const _sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
        await _sleep(350);
        loadedData = loadedData.map((d: any, index: number) => ({
          orgIndex: this.displayData.length ? index + this.displayData.length : index,
          ...d
        }));
        this.displayData.push(...loadedData);
        action.loaded(this.displayData.length, this.pageSize);
      } else {
        action.noMore();
      }
    }
  }
  onSelectionAll(key: string, event: Event) {
    const target = event.target as HTMLInputElement;
    if (this.allSelectionCallback) {
      this.allSelectionCallback(this.displayData, target.checked);
    } else {
      this.displayData.forEach(data => {
        data[key] = target.checked;
      });
    }
    this.$emit("selection-all-change", event);
  }
}
</script>
